import { defineStore } from 'pinia'
import { ref, shallowRef } from 'vue'
import nationalCollationData from '@/assets/national_collation_2026.json'
import L from 'leaflet'
import type { ElectionSummary } from '@/modules/election/models/election-summary.model'
import type LeadingParty from '@/modules/election/models/leading_party.model'
import type { LegendItem } from '@/modules/map/models/legend-item.model'
import { COLORS } from '../utils/colors'
import { CONSTANTS } from '../utils/constants'
import type { PrimariesSummary } from '../models/primaries-summary.model'

export const usePrimariesMapStore = defineStore('primariesMap', () => {
    // State
    const map = shallowRef<L.Map | null>(null)
    const geoJsonLayer = shallowRef<L.GeoJSON | null>(null)
    const selectedParty = ref<string>(CONSTANTS.PARTIES.NPP)
    const regions = ref<any[]>([])

    const electionSummary = ref<PrimariesSummary | null>(null)

    const legendItems = ref<LegendItem[]>([
        {
            id: 1,
            label: 'Margin < 2%',
            value: '',
            colorOne: COLORS.WINNER_LEGEND_COLORS_SHADES.BAWUMIA.COLOR_LOW,
            colorTwo: COLORS.WINNER_LEGEND_COLORS_SHADES.KEN.COLOR_LOW
        },
        {
            id: 2,
            label: 'Win 3% - 59%',
            value: '',
            colorOne: COLORS.WINNER_LEGEND_COLORS_SHADES.BAWUMIA.COLOR_MEDIUM,
            colorTwo: COLORS.WINNER_LEGEND_COLORS_SHADES.KEN.COLOR_MEDIUM

        },
        {
            id: 3,
            label: 'Win > 60%',
            value: '',
            colorOne: COLORS.WINNER_LEGEND_COLORS_SHADES.BAWUMIA.COLOR,
            colorTwo: COLORS.WINNER_LEGEND_COLORS_SHADES.KEN.COLOR
        }
    ])

    const searchResults = ref<any[]>([])

    const selectedRegionNames = ref<string[]>([])

    // Actions
    function initializeMap(mapContainer: HTMLElement) {
        map.value = L.map(mapContainer).setView([7.9465, -1.0232], 7) // Center of Ghana
    }

    function loadData() {
        // Load data based on selectedParty
        // @ts-ignore
        const partyData = nationalCollationData[selectedParty.value]
        if (partyData && partyData.regions) {
            regions.value = partyData.regions
            calculateSummary(regions.value)
        }
    }

    function calculateSummary(regionsData: any[]) {
        let totalVotes = 0
        const candidateVotes: Record<string, number> = {}

        // Aggregate votes
        regionsData.forEach(region => {
            // if (region.region === CONSTANTS.REGIONS.HEADQUARTERS) return // Include HQ

            totalVotes += region.valid_votes
            if (region.candidates) {
                for (const [candidate, votes] of Object.entries(region.candidates)) {
                    candidateVotes[candidate] = (candidateVotes[candidate] || 0) + (votes as number)
                }
            }
        })

        // Identify leaders
        const sortedCandidates = Object.entries(candidateVotes)
            .sort(([, a], [, b]) => b - a)

        if (sortedCandidates.length < 2) return

        const [firstRaw, secondRaw, ...othersRaw] = sortedCandidates

        // Simple mapping for colors/images based on name
        const getColor = (name: string) => {
            if (name.includes('BAWUMIA')) return COLORS.CANDIDATES.BAWUMIA
            if (name.includes('KEN')) return COLORS.CANDIDATES.KEN
            if (name.includes('BRYAN')) return COLORS.CANDIDATES.BRYAN
            if (name.includes('ADUTWUM')) return COLORS.CANDIDATES.ADUTWUM
            if (name.includes('AGYEI')) return COLORS.CANDIDATES.AGYEI
            return COLORS.CANDIDATES.OTHERS
        }

        const getImage = (name: string) => {
            if (name.includes('BAWUMIA')) return CONSTANTS.ASSETS.IMAGES.BAWUMIA
            if (name.includes('KEN')) return CONSTANTS.ASSETS.IMAGES.KEN
            if (name.includes('BRYAN')) return CONSTANTS.ASSETS.IMAGES.BRYAN
            if (name.includes('ADUTWUM')) return CONSTANTS.ASSETS.IMAGES.ADUTWUM
            if (name.includes('AGYEI')) return CONSTANTS.ASSETS.IMAGES.AGYEI
            return ''
        }

        const firstLeadingParty: LeadingParty = {
            name: firstRaw[0],
            votes: firstRaw[1],
            percentage: ((firstRaw[1] / totalVotes) * 100).toFixed(1),
            constituencyWon: 0,
            pollingStationWon: 0,
            color: getColor(firstRaw[0]),
            image: getImage(firstRaw[0])
        }

        const secondLeadingParty: LeadingParty = {
            name: secondRaw[0],
            votes: secondRaw[1],
            percentage: ((secondRaw[1] / totalVotes) * 100).toFixed(1),
            constituencyWon: 0,
            pollingStationWon: 0,
            color: getColor(secondRaw[0]),
            image: getImage(secondRaw[0])
        }

        const minorCandidates: LeadingParty[] = othersRaw.map(([name, votes]) => ({
            name: name,
            votes: votes,
            percentage: ((votes / totalVotes) * 100).toFixed(1),
            constituencyWon: 0,
            pollingStationWon: 0,
            color: getColor(name),
            image: getImage(name)
        }))

        // Keep otherLeadingParty for compatibility if needed, but we use minorCandidates
        const otherLeadingParty: LeadingParty = {
            name: 'OTHERS',
            votes: 0,
            percentage: '0',
            constituencyWon: 0,
            pollingStationWon: 0,
            color: COLORS.CANDIDATES.OTHERS,
            image: ''
        }

        const summary: PrimariesSummary = {
            firstLeadingParty,
            secondLeadingParty,
            otherLeadingParty,
            margin: firstRaw[1] - secondRaw[1],
            marginPercentage: ((firstRaw[1] - secondRaw[1]) / totalVotes) * 100,
            minorCandidates
        }
        
        electionSummary.value = summary
    }

    function searchFeatures(query: string) {
        if (!query) {
            searchResults.value = []
            return
        }
        const lowerQuery = query.toLowerCase()
        searchResults.value = regions.value
            .filter(r => r.region.toLowerCase().includes(lowerQuery))
            .map(r => ({
                id: r.region, // Use name as ID for now
                name: r.region,
                type: 'region',
                region: r.region
            }))
    }

    // Helper to match candidate name to key in WINNER_LEGEND_COLORS_SHADES
    function getCandidateKey(name: string): keyof typeof COLORS.WINNER_LEGEND_COLORS_SHADES {
        const upper = name.toUpperCase()
        if (upper.includes('BAWUMIA')) return 'BAWUMIA'
        if (upper.includes('KEN')) return 'KEN'
        if (upper.includes('BRYAN')) return 'BRYAN'
        if (upper.includes('ADUTWUM')) return 'ADUTWUM'
        if (upper.includes('AGYEI')) return 'AGYEI'
        return 'OTHERS'
    }

    function getPartyColors(candidateName: string) {
        const key = getCandidateKey(candidateName)
        // @ts-ignore
        return COLORS.WINNER_LEGEND_COLORS_SHADES[key] || COLORS.WINNER_LEGEND_COLORS_SHADES.OTHERS
    }

    function getRegionFillColor(regionName: string): string {
        // Handle normalization match
        let rName = regionName.toUpperCase().includes('SAVANNA') ? 'SAVANNA' : regionName

        // Case-insensitive find
        const regionData = regions.value.find(r => r.region.toLowerCase() === rName.toLowerCase())

        if (!regionData || !regionData.candidates) return COLORS.LEGEND.LEVEL_1 // Default/Unknown

        // Calculate winner for this region
        const candidates = Object.entries(regionData.candidates as Record<string, number>)
            .sort(([, a], [, b]) => b - a)

        if (candidates.length < 1) return COLORS.LEGEND.LEVEL_1

        const totalVotes = regionData.valid_votes || 1
        const [firstRaw, secondRaw] = candidates

        const firstVotes = firstRaw[1]
        const secondVotes = secondRaw ? secondRaw[1] : 0

        const firstRatio = firstVotes / totalVotes
        const secondRatio = secondVotes / totalVotes

        const thresholdLow = 0.02 // 2% margin
        const thresholdMedium = 0.6 // 60% vote share

        const { COLOR, COLOR_MEDIUM, COLOR_LOW } = getPartyColors(firstRaw[0])

        // Logic from presidential map
        if (Math.abs(firstRatio - secondRatio) <= thresholdLow) {
            return COLOR_LOW
        }

        const leadingRatio = Math.max(firstRatio, secondRatio)
        return leadingRatio < thresholdMedium ? COLOR_MEDIUM : COLOR
    }

    function selectRegion(regionName: string) {
        // Handle Map vs Data name mismatches
        let normalizedName = regionName
        const upper = regionName.toUpperCase()

        if (upper.includes('SAVANNA')) {
            normalizedName = 'SAVANNA'
        }

        const index = selectedRegionNames.value.indexOf(normalizedName)
        if (index > -1) {
            // Deselect
            selectedRegionNames.value.splice(index, 1)
        } else {
            // Add
            selectedRegionNames.value.push(normalizedName)
        }

        if (selectedRegionNames.value.length > 0) {
            const selectedData = regions.value.filter(r => selectedRegionNames.value.includes(r.region))
            calculateSummary(selectedData)
        } else {
            electionSummary.value = null
        }
    }

    function resetStore() {
        if (map.value) {
            map.value.setView([7.9465, -1.0232], 7)
        }
        selectedParty.value = CONSTANTS.PARTIES.NPP
        selectedRegionNames.value = []
        loadData() // Reloads all data
    }

    function zoomToFeature(bounds: L.LatLngBounds) {
        map.value?.flyToBounds(bounds, {
            duration: 1.0,
            easeLinearity: 0.5
        })
    }

    return {
        map,
        geoJsonLayer,
        selectedParty,
        regions,
        electionSummary,
        legendItems,
        searchResults,
        selectedRegionNames,
        initializeMap,
        loadData,
        searchFeatures,
        selectRegion,
        getRegionFillColor,
        zoomToFeature,
        resetStore
    }
})
